"use client";

import React, { useState } from "react";
import { Database, HardDrive, MessageSquare, ShieldCheck, Zap, Layers3 } from "lucide-react";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
type ResourceType = "vectors" | "storage" | "queries";
interface PackData {
  id: string;
  name: string;
  description: string;
  price: string;
  resources: {
    vectors: string;
    storage: string;
    queries: string;
  };
  ctaText: string;
}
const packs: PackData[] = [{
  id: "pack-s",
  name: "Pack S",
  description: "A smaller expansion for controlled growth in one resource category.",
  price: "$199",
  resources: {
    vectors: "+1M vectors",
    storage: "+10GB indexed storage",
    queries: "+50k queries / month"
  },
  ctaText: "Add Pack S"
}, {
  id: "pack-m",
  name: "Pack M",
  description: "A standard expansion for growing teams, agencies, and tenant environments.",
  price: "$499",
  resources: {
    vectors: "+3M vectors",
    storage: "+30GB indexed storage",
    queries: "+150k queries / month"
  },
  ctaText: "Add Pack M"
}, {
  id: "pack-l",
  name: "Pack L",
  description: "A larger expansion for higher-volume usage and more demanding delivery models.",
  price: "$999",
  resources: {
    vectors: "+8M vectors",
    storage: "+80GB indexed storage",
    queries: "+400k queries / month"
  },
  ctaText: "Add Pack L"
}];
function CapacityCard({
  pack,
  index
}: {
  pack: PackData;
  index: number;
}) {
  const [selectedResource, setSelectedResource] = useState<ResourceType>("vectors");
  const handleValueChange = (value: string) => {
    if (value) {
      setSelectedResource(value as ResourceType);
    }
  };
  return <div data-index={index} className="flex h-full">
      <Card className="flex w-full flex-col rounded-2xl border border-border/70 shadow-[0_10px_30px_-18px_rgba(0,0,0,0.12)] transition-all duration-200 hover:shadow-[0_18px_40px_-18px_rgba(0,0,0,0.15)]">
        <CardHeader>
          <div className="mb-2 flex items-center justify-between">
            <CardTitle className="text-2xl text-foreground">{pack.name}</CardTitle>
            <div className="rounded-lg bg-primary/10 p-2.5">
              <Zap className="h-5 w-5 text-primary" />
            </div>
          </div>

          <CardDescription className="min-h-[48px] text-sm leading-relaxed text-muted-foreground">
            {pack.description}
          </CardDescription>
        </CardHeader>

        <CardContent className="flex flex-1 flex-col gap-6">
          <div className="space-y-3">
            <span className="text-sm font-semibold text-foreground">
              Choose one resource to expand
            </span>

            <ToggleGroup type="single" value={selectedResource} onValueChange={handleValueChange} variant="outline" className="w-full justify-start rounded-lg bg-secondary/40 p-1">
              <ToggleGroupItem value="vectors" className="flex-1 text-xs data-[state=on]:bg-background data-[state=on]:shadow-xs sm:text-sm">
                <Database className="mr-2 hidden h-4 w-4 sm:inline-block" />
                Vectors
              </ToggleGroupItem>
              <ToggleGroupItem value="storage" className="flex-1 text-xs data-[state=on]:bg-background data-[state=on]:shadow-xs sm:text-sm">
                <HardDrive className="mr-2 hidden h-4 w-4 sm:inline-block" />
                Storage
              </ToggleGroupItem>
              <ToggleGroupItem value="queries" className="flex-1 text-xs data-[state=on]:bg-background data-[state=on]:shadow-xs sm:text-sm">
                <MessageSquare className="mr-2 hidden h-4 w-4 sm:inline-block" />
                Queries
              </ToggleGroupItem>
            </ToggleGroup>
          </div>

          <div className="flex min-h-[132px] flex-col items-center justify-center rounded-xl border border-accent bg-accent/30 p-6 text-center">
            <span className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              This pack adds
            </span>
            <span className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
              {pack.resources[selectedResource]}
            </span>
          </div>

          <div className="rounded-lg border border-border/60 bg-background p-4 text-sm leading-relaxed text-muted-foreground">
            Capacity Packs expand <strong className="text-foreground">one resource type only</strong>.
            They do not increase storage, vectors, and queries at the same time.
          </div>
        </CardContent>

        <CardFooter className="flex flex-col gap-4 border-t border-border/50 pt-6">
          <div className="flex w-full items-end justify-between gap-4">
            <div className="flex flex-col">
              <span className="text-3xl font-bold text-foreground">{pack.price}</span>
              <span className="text-sm text-muted-foreground">/ month per pack</span>
            </div>

            <Button variant="default" size="lg" className="h-11 px-5">
              {pack.ctaText}
            </Button>
          </div>

          <div className="flex w-full items-center justify-center gap-2 rounded-md bg-secondary/30 py-2 text-xs text-muted-foreground">
            <ShieldCheck className="h-3.5 w-3.5" />
            <span>Hard caps remain enforced. No surprise overages.</span>
          </div>
        </CardFooter>
      </Card>
    </div>;
}
export default function CapacityPacksExplained() {
  return <section id="capacity-packs-explained" className="w-full bg-background py-16 lg:py-20">
      <div className="mx-auto max-w-7xl px-6 md:px-8">
        <div className="mx-auto mb-12 flex max-w-3xl flex-col items-center space-y-4 text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-sm font-medium text-primary">
            <Layers3 className="size-4" />
            Capacity Packs
          </div>

          <h2 className="text-3xl font-bold tracking-tight text-foreground md:text-4xl">
            Capacity Packs for predictable scaling
          </h2>
          <p className="text-lg leading-relaxed text-muted-foreground">
            Capacity Packs are monthly add-ons that expand storage, vectors, or query limits without
            changing your platform structure. They help teams and agencies scale in a controlled way
            while keeping hard caps and platform behavior predictable.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
          {packs.map((pack, i) => <CapacityCard key={pack.id} pack={pack} index={i} />)}
        </div>
      </div>
    </section>;
}